import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronRight, ExternalLink, LayoutDashboard, Plus } from 'lucide-react';
import { Heading } from '../layout';
import { PillTabBar, ScheduleCalendar } from '../reusable';
import ProspectEventsPanel from './ProspectEventsPanel';
import Table3 from '../organizations/Table3';
import ContactsTable3 from './ContactsTable3';
import ContactActivitiesTable3 from './ContactActivitiesTable3';
import ProspectTrackingTable3 from './ProspectTrackingTable3';
import { ContactActivitiesForm, ContactsForm, OrganizationsForm, serializeOrganizationChannels } from '../forms';
import API_BASE_URL from '../../config/api';
import { useBusinessUnitApi } from '../../contexts/BusinessUnitContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUIColors } from '../../hooks/useTheme';
import OrganizationRubricsSection from './OrganizationRubricsSection';
import PeriodSummaryTable from './PeriodSummaryTable';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  parseDateSafe,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';
import { getActiveFunnelStageOptions } from './commercialCatalogDefaults';
import { funnelStagePresentation, pipelineFunnelStagesFromOptions, resolveOrganizationFunnelStage, applyFunnelFloor, quotationFunnelFloor } from './funnelStageUtils';

const DEFAULT_PIPELINE_STAGES = [
  'Identificado',
  'Contactado',
  'Calificado',
  'Propuesta',
  'Negociación',
  'Cierre',
  'Perdido',
];

const formatRut = (value = '') => {
  const clean = String(value || '')
    .toUpperCase()
    .replace(/[^0-9K]/g, '');
  if (clean.length <= 1) return clean;
  const dv = clean.slice(-1);
  const numberPart = clean.slice(0, -1);
  const withDots = numberPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withDots}-${dv}`;
};
const normalizeOrgName = (value = '') =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const SEGMENT_OPTIONS = ['B2B', 'B2C', 'B2G', 'B2B2C', 'Sin Asignar'];
const IDENTIFICATION_ALLOWED_SEGMENTS = ['B2B'];

const TRACKING_DUE_COLORS = {
  Vencida: '#ef4444',
  'Por vencer': '#d97706',
  Pendiente: '#f59e0b',
  Programada: '#2563eb',
  Completa: '#16a34a',
};
const ACTIVITY_STATUS_COLORS = {
  Programada: '#2563eb',
  Pendiente: '#f59e0b',
  Completa: '#16a34a',
};
const SECTION_ITEMS = [
  { key: 'pipeline', label: 'Pipeline', icon: LayoutDashboard },
  { key: 'events', label: 'Eventos', icon: CalendarDays },
];
const PROSPECT_SUMMARY_METRIC_OPTIONS = [
  { value: 'count', label: 'Cantidad' },
  { value: 'amount', label: 'Monto potencial' },
];
const formatSummaryCount = (value) => Number(value || 0).toLocaleString('es-CL');
const formatSummaryMoney = (value) =>
  Number(value || 0).toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  });
const getProspectAnchorDate = (organization) =>
  organization?.created_at || organization?.updated_at || organization?.date || null;
const getTrackingAnchorDate = (row) =>
  row?.next_step_date || row?.follow_up_reminder_date || row?.activity_date || row?.date || null;
const toTrackingDateYmd = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};
const EMPTY_ORG_FORM = {
  org_code: 'Se asigna automáticamente',
  org_name: '',
  rut: '',
  segment: 'B2B',
  channel: '',
  created_by: '',
  assigned_user_id: '',
  website: '',
  linkedin: '',
  industry: '',
  subindustry: '',
  email: '',
  phone: '',
  address: '',
  commune: '',
  region: '',
  country: 'Chile',
  contacts: [],
};
const EMPTY_CONTACT_FORM = {
  contact_code: 'Se asigna automáticamente',
  full_name: '',
  role: '',
  email: '',
  linkedin: '',
  phone: '',
  organization_id: '',
  status: 'Activo',
  source: '',
  source_details: '',
};
const EMPTY_ACTIVITY_FORM = {
  contact_id: '',
  contact_ids: [],
  activity_date: '',
  action: '',
  status: 'Pendiente',
  notes: '',
  response: '',
  no_response: false,
  next_step: '',
  next_step_detail: '',
  next_step_date: '',
  has_next_steps: false,
  has_follow_up_reminder: false,
  follow_up_reminder_date: '',
  owner_name: '',
  funnel_stage: '',
};

const buildPipelineStageStyles = (funnelOptions = []) => {
  const styles = {};
  (Array.isArray(funnelOptions) ? funnelOptions : []).forEach((opt) => {
    const label = String(opt.label || opt.value || '').trim();
    if (!label) return;
    const tone = funnelStagePresentation(label, true);
    styles[label] = {
      accent: tone.border,
      pillBg: tone.bg,
      pillText: tone.color,
      border: tone.border,
    };
  });
  return styles;
};

const formatPipelineMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount === 0) return '$0';
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(amount / 1_000)}k`;
  return `$${amount.toLocaleString('es-CL')}`;
};

const PipelineCard = ({
  card,
  accent,
  colors,
  isDarkMode = false,
  isExpanded,
  onToggle,
  onOpen,
}) => {
  const amountLabel = formatPipelineMoney(card.amount);
  const fullAmount = `$${Number(card.amount || 0).toLocaleString('es-CL')}`;
  const chipBg = isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc';

  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: '6px',
        background: colors.cardBg,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 6px 4px 0',
        }}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '2px 2px 2px 8px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span style={{ color: colors.muted, display: 'inline-flex', flexShrink: 0 }}>
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              color: colors.text,
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={card.company}
          >
            {card.company}
          </span>
          <span
            style={{
              flexShrink: 0,
              color: '#ea580c',
              fontSize: '11px',
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {amountLabel}
          </span>
        </button>
        {typeof onOpen === 'function' ? (
          <button
            type="button"
            title="Abrir ficha de prospección"
            aria-label={`Abrir ficha de ${card.company || 'lead'}`}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(card);
            }}
            style={{
              flexShrink: 0,
              width: 28,
              height: 28,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: isDarkMode ? 'rgba(255,255,255,0.04)' : '#fff',
              color: colors.text,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <ExternalLink size={13} />
          </button>
        ) : null}
      </div>

      {isExpanded ? (
        <div
          style={{
            padding: '0 8px 8px 28px',
            display: 'grid',
            gap: '4px',
            fontSize: '11px',
            color: colors.muted,
            lineHeight: 1.35,
          }}
        >
          <div>
            <span style={{ color: colors.text, fontWeight: 600 }}>{card.owner || 'Sin asignar'}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {card.segment ? (
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: '999px',
                  border: `1px solid ${colors.border}`,
                  background: chipBg,
                }}
              >
                {card.segment}
              </span>
            ) : null}
            {card.status ? (
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: '999px',
                  border: `1px solid ${colors.border}`,
                  background: chipBg,
                }}
              >
                {card.status}
              </span>
            ) : null}
          </div>
          {(card.channel || card.industry) && (
            <div>{[card.channel, card.industry].filter(Boolean).join(' · ')}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
            <span>{Number(card.contacts_count || 0)} contactos</span>
            <span style={{ color: '#ea580c', fontWeight: 700 }}>{fullAmount}</span>
          </div>
          {typeof onOpen === 'function' ? (
            <button
              type="button"
              onClick={() => onOpen(card)}
              style={{
                marginTop: 4,
                height: 28,
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: isDarkMode ? 'rgba(37,99,235,0.15)' : '#eff6ff',
                color: isDarkMode ? '#93c5fd' : '#1d4ed8',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <ExternalLink size={12} /> Abrir ficha
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const PipelineBoard = ({
  cardsByStage = {},
  stages = DEFAULT_PIPELINE_STAGES,
  stageStyles = {},
  isDarkMode = false,
  onOpenCard,
}) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const colors = isDarkMode
    ? {
        boardBg: '#0f1116',
        columnBg: '#161922',
        cardBg: '#1b1f2b',
        border: '#2f3442',
        text: '#e5e7eb',
        muted: '#9ca3af',
      }
    : {
        boardBg: '#f8fafc',
        columnBg: '#ffffff',
        cardBg: '#ffffff',
        border: '#e5e7eb',
        text: '#111827',
        muted: '#6b7280',
      };

  const toggleExpanded = (cardId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '12px', background: colors.boardBg }}>
      <div style={{ marginBottom: '10px' }}>
        <h4 style={{ margin: 0, color: colors.text, fontSize: '18px' }}>Pipeline</h4>
        <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: '12px' }}>
          Visualiza y mueve oportunidades por etapa comercial. Haz clic en una tarjeta para ver más detalle.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))`,
          gap: '10px',
          overflowX: 'auto',
        }}
      >
        {stages.map((stage) => {
          const cards = cardsByStage[stage] || [];
          const stageStyle = stageStyles[stage] || funnelStagePresentation(stage, isDarkMode);
          const accent = stageStyle.accent || stageStyle.border || '#64748b';
          const pillBg = stageStyle.pillBg || stageStyle.bg || 'rgba(100,116,139,0.18)';
          const pillText = stageStyle.pillText || stageStyle.color || '#cbd5e1';
          const border = stageStyle.border || accent;
          return (
            <div
              key={stage}
              style={{
                background: colors.columnBg,
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                overflow: 'hidden',
                minWidth: '160px',
              }}
            >
              <div
                style={{
                  borderTop: `3px solid ${accent}`,
                  padding: '8px 10px',
                  borderBottom: `1px solid ${colors.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ color: pillText, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                  {stage}
                </span>
                <span
                  style={{
                    minWidth: '20px',
                    height: '20px',
                    borderRadius: '999px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: pillText,
                    border: `1px solid ${border}`,
                    background: pillBg,
                  }}
                >
                  {cards.length}
                </span>
              </div>

              <div
                style={{
                  padding: '6px',
                  display: 'grid',
                  gap: '4px',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                }}
              >
                {cards.length === 0 ? (
                  <div
                    style={{
                      border: `1px dashed ${colors.border}`,
                      borderRadius: '8px',
                      padding: '10px',
                      color: colors.muted,
                      fontSize: '12px',
                      textAlign: 'center',
                    }}
                  >
                    Sin oportunidades
                  </div>
                ) : (
                  cards.map((card) => (
                    <PipelineCard
                      key={card.id}
                      card={card}
                      accent={accent}
                      colors={colors}
                      isDarkMode={isDarkMode}
                      isExpanded={expandedIds.has(card.id)}
                      onToggle={() => toggleExpanded(card.id)}
                      onOpen={onOpenCard}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CompanyInfoModal = ({
  isOpen,
  onClose,
  isDarkMode = false,
  organization,
  contacts = [],
  activities = [],
  selectedContactId = null,
  onSelectContact,
  activitiesLoading = false,
  activityContactFilterId = '',
  onChangeActivityContactFilter,
  onAddContact,
  onEditContact,
  onDeleteContact,
  onAddActivity,
  onViewActivity,
  onDeleteActivity,
  onContactsChanged,
}) => {
  if (!isOpen || !organization) return null;
  const colors = isDarkMode
    ? {
        overlay: 'rgba(0,0,0,0.55)',
        panel: '#18181b',
        panelBorder: '#2f2f35',
        text: '#d4d4d8',
        title: '#f4f4f5',
        sectionBg: '#141417',
        sectionBorder: '#2a2a31',
        muted: '#a1a1aa',
      }
    : {
        overlay: 'rgba(15,23,42,0.35)',
        panel: '#ffffff',
        panelBorder: '#e5e7eb',
        text: '#1f2937',
        title: '#111827',
        sectionBg: '#f9fafb',
        sectionBorder: '#e5e7eb',
        muted: '#6b7280',
      };

  const infoRows = [
    ['Código', organization.org_code || '-'],
    ['Nombre', organization.org_name || '-'],
    ['RUT', organization.rut || '-'],
    ['Subunidad', organization.subunit || '-'],
    ['Segmento', organization.segment || '-'],
    ['Canal', organization.channel || '-'],
    ['Web', organization.website || '-'],
    ['LinkedIn', organization.linkedin || '-'],
    ['Industria', organization.industry || '-'],
    ['Subindustria', organization.subindustry || '-'],
    ['Correo', organization.email || '-'],
    ['Fono', organization.phone || '-'],
    ['Dirección', organization.address || '-'],
    ['Comuna', organization.commune || '-'],
    ['Región', organization.region || '-'],
    ['País', organization.country || '-'],
    ['# Contactos', Number(organization.contacts_count || 0).toLocaleString('es-CL')],
    ['# Cotizaciones', Number(organization.quotations_count || 0).toLocaleString('es-CL')],
    ['# Ventas', Number(organization.sales_count || 0).toLocaleString('es-CL')],
    ['Monto Ventas', `$ ${Number(organization.sales_amount || 0).toLocaleString('es-CL')}`],
    ['Estado', organization.status || '-'],
    ['Etapa funnel', organization.funnel_stage || '-'],
    ['Responsable', organization.assignee_name || '-'],
    ['Creado por', organization.created_by || '-'],
    ['Creado en', organization.created_at || '-'],
    ['Actualizado en', organization.updated_at || '-'],
  ];

  const selectedContact = contacts.find((contact) => Number(contact.id) === Number(selectedContactId));
  const filteredContactLabel =
    activityContactFilterId
      ? contacts.find((c) => String(c.id) === String(activityContactFilterId))?.full_name ||
        selectedContact?.full_name ||
        'contacto seleccionado'
      : null;

  const handleBulkDeleteContacts = async (ids) => {
    await onDeleteContact?.(ids.map((id) => ({ id })));
  };

  const handleBulkDeleteActivities = async (ids) => {
    await onDeleteActivity?.(ids.map((id) => ({ id })));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 11000, background: colors.overlay, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px' }}>
      <div
        style={{
          width: 'min(1400px, 96vw)',
          maxHeight: '92vh',
          overflowY: 'auto',
          background: colors.panel,
          border: `1px solid ${colors.panelBorder}`,
          borderRadius: '12px',
          padding: '16px',
          color: colors.text,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, color: colors.title, fontSize: '18px' }}>Detalle Empresa y Relación Comercial</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              height: '32px',
              borderRadius: '8px',
              border: `1px solid ${colors.sectionBorder}`,
              background: 'transparent',
              color: colors.text,
              cursor: 'pointer',
              padding: '0 12px',
            }}
          >
            Cerrar
          </button>
        </div>

        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ border: `1px solid ${colors.sectionBorder}`, borderRadius: '10px', background: colors.sectionBg, padding: '12px' }}>
            <h4 style={{ margin: '0 0 10px', color: colors.title, fontSize: '14px' }}>1) Información de la Empresa</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px 12px' }}>
              {infoRows.map(([label, value]) => (
                <div key={label} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '11px', color: colors.muted }}>{label}</div>
                  <div style={{ fontSize: '13px', color: colors.text, wordBreak: 'break-word' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          <ContactsTable3
            rows={contacts}
            isDarkMode={isDarkMode}
            tableTitle="2) Contactos Asociados"
            addButtonLabel="Nuevo contacto"
            subtitleOverride={
              selectedContactId
                ? `${contacts.length} contactos · seleccionado: ${selectedContact?.full_name || 'contacto'}`
                : `${contacts.length} contactos · haz click en una fila para filtrar actividades`
            }
            hideOrganizationColumn
            activeRowId={selectedContactId}
            onAdd={onAddContact}
            onRowNameClick={onEditContact}
            onRowClick={(row) => onSelectContact?.(row?.id)}
            onBulkDelete={onDeleteContact ? handleBulkDeleteContacts : undefined}
          />

          <ContactActivitiesTable3
            rows={activities}
            contacts={contacts}
            loading={activitiesLoading}
            isDarkMode={isDarkMode}
            tableTitle="3) Actividades de la Organización"
            addButtonLabel="Nueva actividad"
            subtitleOverride={
              activitiesLoading
                ? 'Cargando actividades de la organización...'
                : [
                    filteredContactLabel
                      ? `Mostrando actividades de ${filteredContactLabel}`
                      : 'Mostrando todas las actividades de la organización',
                    organization?.assignee_name
                      ? `Responsable del prospecto: ${organization.assignee_name}`
                      : 'Sin responsable asignado',
                    'Cualquier usuario puede registrar actividades',
                  ].join(' · ')
            }
            contactFilterId={activityContactFilterId}
            onChangeContactFilter={onChangeActivityContactFilter}
            onAdd={onAddActivity}
            onRowNameClick={onViewActivity}
            onBulkDelete={onDeleteActivity ? handleBulkDeleteActivities : undefined}
          />

          <div style={{ border: `1px solid ${colors.sectionBorder}`, borderRadius: '10px', background: colors.sectionBg, padding: '12px' }}>
            <OrganizationRubricsSection
              organization={organization}
              contacts={contacts}
              isDarkMode={isDarkMode}
              onContactsChanged={onContactsChanged}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const PaintProspectingWorkspace = ({ isDarkMode = false }) => {
  const { user, loading: authLoading } = useAuth();
  const ui = useUIColors();
  const { apiClient, unit } = useBusinessUnitApi();
  const sessionUserName = String(user?.full_name || user?.username || '').trim();
  const funnelStageOptions = useMemo(() => getActiveFunnelStageOptions(undefined, unit), [unit]);
  const pipelineStages = useMemo(
    () => pipelineFunnelStagesFromOptions(funnelStageOptions),
    [funnelStageOptions]
  );
  const pipelineStageStyles = useMemo(
    () => buildPipelineStageStyles(funnelStageOptions),
    [funnelStageOptions]
  );
  const activityFunnelStageOptions = useMemo(
    () => [{ value: '', label: 'Sin etapa' }, ...funnelStageOptions],
    [funnelStageOptions]
  );
  const resolveOrganizationPipelineStage = (organization) => {
    const stored = String(organization?.funnel_stage || '').trim();
    const derivedFromContacts = resolveOrganizationFunnelStage(
      contactsData
        .filter((contact) => Number(contact.organization_id) === Number(organization?.id))
        .map((contact) => contact.funnel_stage)
    );
    const baseStage = stored || derivedFromContacts;
    const orgQuotes = (quotationsData || []).filter(
      (quotation) => Number(quotation?.organization_id) === Number(organization?.id)
    );
    const stage = applyFunnelFloor(baseStage, quotationFunnelFloor(orgQuotes));
    if (stage && pipelineStages.includes(stage)) return stage;
    const matched = pipelineStages.find(
      (label) => label.toLowerCase() === String(stage || '').trim().toLowerCase()
    );
    if (matched) return matched;
    return pipelineStages[0] || 'Identificado';
  };
  const [usersData, setUsersData] = useState([]);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const [quotedTotalsByOrg, setQuotedTotalsByOrg] = useState({});
  const [quotationsData, setQuotationsData] = useState([]);
  const [calendarStatusFilter, setCalendarStatusFilter] = useState('all');
  const [calendarShowQuotations, setCalendarShowQuotations] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedExecutive, setSelectedExecutive] = useState('');
  const [sectionTab, setSectionTab] = useState('pipeline');
  const [listTab, setListTab] = useState('prospects');
  const [assigningEventId, setAssigningEventId] = useState(null);
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState('all');
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [summaryMetric, setSummaryMetric] = useState('count');
  const [showOrgFormModal, setShowOrgFormModal] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [orgFormData, setOrgFormData] = useState(EMPTY_ORG_FORM);
  const [orgFormError, setOrgFormError] = useState('');
  const [orgOriginalContactIds, setOrgOriginalContactIds] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [organizationActivities, setOrganizationActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activityContactFilterId, setActivityContactFilterId] = useState('');
  const [trackingRows, setTrackingRows] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [showContactFormModal, setShowContactFormModal] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactFormData, setContactFormData] = useState(EMPTY_CONTACT_FORM);
  const [contactFormError, setContactFormError] = useState('');
  const [showActivityFormModal, setShowActivityFormModal] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [activityFormMode, setActivityFormMode] = useState('create');
  const [isSavingActivity, setIsSavingActivity] = useState(false);
  const [activityFormData, setActivityFormData] = useState(EMPTY_ACTIVITY_FORM);
  const [activityFormError, setActivityFormError] = useState('');

  const loadTrackingRows = async () => {
    setTrackingLoading(true);
    setTrackingError('');
    try {
      const trackingRaw = await apiClient(`${API_BASE_URL}/api/contacts/activities/tracking`);
      setTrackingRows(Array.isArray(trackingRaw) ? trackingRaw : []);
    } catch (error) {
      console.error('Error loading tracking for prospecting:', error);
      setTrackingRows([]);
      setTrackingError(error?.message || 'No se pudieron cargar los seguimientos.');
    } finally {
      setTrackingLoading(false);
    }
  };

  const buildQuotedTotalsByOrg = (quotationsRaw) => {
    const map = {};
    (Array.isArray(quotationsRaw) ? quotationsRaw : []).forEach((quotation) => {
      const organizationId = Number(quotation?.organization_id);
      if (!organizationId) return;
      map[organizationId] = (map[organizationId] || 0) + Number(quotation?.total_amount || 0);
    });
    return map;
  };

  const loadBaseData = async () => {
    try {
      const [usersRaw, organizationsRaw, contactsRaw, quotationsRaw] = await Promise.all([
        apiClient(`${API_BASE_URL}/api/users/`),
        apiClient(`${API_BASE_URL}/api/organizations/`),
        apiClient(`${API_BASE_URL}/api/contacts/`),
        apiClient(`${API_BASE_URL}/api/quotations/`).catch((error) => {
          console.error('Error loading quotation totals for prospecting:', error);
          return [];
        }),
      ]);
      const users = Array.isArray(usersRaw) ? usersRaw : [];
      const organizations = Array.isArray(organizationsRaw) ? organizationsRaw : [];
      const contacts = Array.isArray(contactsRaw) ? contactsRaw : [];
      const quotations = Array.isArray(quotationsRaw) ? quotationsRaw : [];
      setUsersData(users);
      setOrganizationsData(organizations);
      setContactsData(contacts);
      setQuotationsData(quotations);
      setQuotedTotalsByOrg(buildQuotedTotalsByOrg(quotations));
      await loadTrackingRows();
      return { users, organizations, contacts };
    } catch (error) {
      console.error('Error loading base data for prospecting:', error);
      setUsersData([]);
      setOrganizationsData([]);
      setContactsData([]);
      setQuotationsData([]);
      setQuotedTotalsByOrg({});
      await loadTrackingRows();
      return { users: [], organizations: [], contacts: [] };
    }
  };

  useEffect(() => {
    if (authLoading || !user) return;
    loadBaseData();
  }, [authLoading, user]);

  const modalContacts = useMemo(
    () =>
      selectedOrganization
        ? contactsData.filter((contact) => Number(contact.organization_id) === Number(selectedOrganization.id))
        : [],
    [contactsData, selectedOrganization]
  );
  const modalOrganizationOptions = useMemo(
    () =>
      selectedOrganization
        ? [
            {
              value: String(selectedOrganization.id),
              label: selectedOrganization.org_name || `ORG ${selectedOrganization.id}`,
            },
          ]
        : [{ value: '', label: 'Seleccionar organización' }],
    [selectedOrganization]
  );
  const activityContactOptions = useMemo(
    () =>
      modalContacts.map((contact) => ({
        value: String(contact.id),
        label: contact.full_name || contact.contact_code || `CTC ${contact.id}`,
      })),
    [modalContacts]
  );

  const loadActivitiesForOrganization = useCallback(async (organizationId) => {
    if (!organizationId) {
      setOrganizationActivities([]);
      return;
    }
    setActivitiesLoading(true);
    try {
      const rawData = await apiClient(
        `${API_BASE_URL}/api/contacts/organizations/${organizationId}/activities`
      );
      setOrganizationActivities(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      console.error('Error loading organization activities:', error);
      setOrganizationActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    if (!showCompanyModal) return;
    loadActivitiesForOrganization(selectedOrganization?.id);
  }, [showCompanyModal, selectedOrganization?.id, loadActivitiesForOrganization]);

  const identificationData = useMemo(
    () =>
      organizationsData
        .filter((organization) =>
          IDENTIFICATION_ALLOWED_SEGMENTS.includes(String(organization.segment || '').trim())
        )
        .map((organization) => {
          const orgContactsCount = contactsData.filter(
            (contact) => Number(contact.organization_id) === Number(organization.id)
          ).length;
          const assignedId = organization.assigned_user_id
            ? Number(organization.assigned_user_id)
            : null;
          const assignedUser = assignedId
            ? usersData.find((user) => Number(user.id) === assignedId)
            : null;
          const ownerName =
            String(organization.assignee_name || '').trim() ||
            assignedUser?.full_name ||
            assignedUser?.user_code ||
            assignedUser?.username ||
            'Sin asignar';

          return {
            prospect_code: organization.org_code || `ORG-${organization.id}`,
            company: organization.org_name || 'Sin nombre',
            segment: SEGMENT_OPTIONS.includes(organization.segment) ? organization.segment : 'Sin Asignar',
            channel: organization.channel || '-',
            industry: organization.industry || '-',
            status: organization.status || '-',
            owner_user_id: assignedId || '',
            owner: ownerName,
            contacts_count: orgContactsCount || Number(organization.contacts_count || 0),
            amount: Number(quotedTotalsByOrg[Number(organization.id)] || 0),
            pipeline_stage: resolveOrganizationPipelineStage(organization),
            organization_id: organization.id,
          };
        })
        .sort((a, b) => String(a.company || '').localeCompare(String(b.company || ''), 'es', { sensitivity: 'base' })),
    [organizationsData, contactsData, usersData, pipelineStages, quotedTotalsByOrg, quotationsData]
  );

  const industryOptions = useMemo(() => {
    const unique = Array.from(new Set(identificationData.map((row) => row.industry).filter(Boolean)));
    return unique.sort();
  }, [identificationData]);

  const executiveOptions = useMemo(
    () => usersData.map((user) => ({ value: String(user.id), label: user.full_name || user.user_code || `USR ${user.id}` })),
    [usersData]
  );
  const activityExecutiveOptions = useMemo(
    () => [
      { value: '', label: 'Seleccionar ejecutivo' },
      ...usersData.map((user) => ({
        value: user.full_name || user.user_code || `USR ${user.id}`,
        label: user.full_name || user.user_code || `USR ${user.id}`,
      })),
    ],
    [usersData]
  );
  const orgExecutiveOptions = useMemo(
    () => [
      { value: '', label: 'Seleccionar responsable' },
      ...usersData.map((user) => ({
        value: String(user.id),
        label: user.full_name || user.user_code || `USR ${user.id}`,
      })),
    ],
    [usersData]
  );

  const filteredIdentificationData = useMemo(
    () =>
      identificationData.filter((row) => {
        const industryMatch = !selectedIndustry || row.industry === selectedIndustry;
        const executiveMatch = !selectedExecutive || String(row.owner_user_id) === selectedExecutive;
        return industryMatch && executiveMatch;
      }),
    [identificationData, selectedIndustry, selectedExecutive]
  );
  const pipelineCardsByStage = useMemo(() => {
    const grouped = pipelineStages.reduce((acc, stage) => ({ ...acc, [stage]: [] }), {});
    const fallbackStage = pipelineStages[0] || 'Identificado';
    identificationData.forEach((row) => {
      const stage = pipelineStages.includes(row.pipeline_stage) ? row.pipeline_stage : fallbackStage;
      grouped[stage].push({
        id: row.organization_id,
        company: row.company,
        owner: row.owner,
        amount: row.amount,
        segment: row.segment,
        status: row.status,
        channel: row.channel,
        industry: row.industry,
        contacts_count: row.contacts_count,
      });
    });
    pipelineStages.forEach((stage) => {
      grouped[stage] = grouped[stage].sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0));
    });
    return grouped;
  }, [identificationData, pipelineStages]);

  const prospectUniverse = useMemo(
    () =>
      organizationsData.filter((organization) =>
        IDENTIFICATION_ALLOWED_SEGMENTS.includes(String(organization.segment || '').trim())
      ),
    [organizationsData]
  );

  const prospectDateValues = useMemo(
    () => collectDates(prospectUniverse, getProspectAnchorDate),
    [prospectUniverse]
  );

  const availableYears = useMemo(
    () => getAvailableYearsFromDates(prospectDateValues),
    [prospectDateValues]
  );

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: prospectDateValues,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Resumen prospección mensual',
          annual: 'Resumen prospección anual',
        },
      }),
    [periodFilter, prospectDateValues, secondaryYear, secondaryMonth]
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (
      periodFilter === 'annual' ||
      periodFilter === 'annual_ytd' ||
      periodFilter === 'quarterly' ||
      periodFilter === 'semestral'
    ) {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly') {
      if (!availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear(String(availableYears[availableYears.length - 1]));
      }
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (
      nextPeriod === 'annual' ||
      nextPeriod === 'annual_ytd' ||
      nextPeriod === 'quarterly' ||
      nextPeriod === 'semestral'
    ) {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(
        String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear)
      );
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const trackingCalendarEvents = useMemo(() => {
    const prospectOrgIds = new Set(
      (organizationsData || [])
        .filter((organization) =>
          IDENTIFICATION_ALLOWED_SEGMENTS.includes(String(organization.segment || '').trim())
        )
        .map((organization) => Number(organization.id))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    const activityEvents = (trackingRows || [])
      .map((row, idx) => {
        const organizationId = Number(row.organization_id || 0);
        if (organizationId > 0 && !prospectOrgIds.has(organizationId)) return null;
        const activityStatus = String(row.status || '').trim() || 'Pendiente';
        // Fecha visible: programadas/completas usan la fecha de la actividad;
        // pendientes priorizan recordatorio / próximo paso.
        const dateSource =
          activityStatus === 'Programada' || activityStatus === 'Completa'
            ? row.activity_date || row.next_step_date || row.follow_up_reminder_date || ''
            : row.follow_up_reminder_date ||
              row.next_step_date ||
              row.activity_date ||
              '';
        const date = toTrackingDateYmd(dateSource);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
        const contactLabel =
          Array.isArray(row.contact_names) && row.contact_names.length
            ? row.contact_names.join(', ')
            : row.contact_name || 'Contacto';
        const orgLabel = row.organization_name || 'Organización';
        const ownerName = String(row.owner_name || '').trim();
        const stepLabel =
          String(row.action || '').trim() ||
          String(row.next_step || '').trim() ||
          (row.has_follow_up_reminder || row.follow_up_reminder_date
            ? 'Recordatorio de seguimiento'
            : 'Seguimiento');
        return {
          id: `act-${row.id || idx}`,
          kind: 'activity',
          date,
          title: orgLabel,
          subtitle: [contactLabel, stepLabel, ownerName].filter(Boolean).join(' · '),
          status: [activityStatus, row.due_state !== activityStatus ? row.due_state : null]
            .filter(Boolean)
            .join(' · '),
          color:
            ACTIVITY_STATUS_COLORS[activityStatus] ||
            TRACKING_DUE_COLORS[row.due_state] ||
            '#2563eb',
          organizationId: row.organization_id,
          organizationName: orgLabel,
          contactId: row.contact_id,
          activityId: row.id,
          owner: ownerName,
          sourceRow: row,
          activityStatus,
        };
      })
      .filter(Boolean);

    const quotationEvents = (quotationsData || [])
      .map((quotation, idx) => {
        const organizationId = Number(quotation.organization_id || 0);
        // Solo cotizaciones de leads B2B visibles en prospección.
        if (!organizationId || !prospectOrgIds.has(organizationId)) return null;
        const quoteStatus = String(quotation.status || '').trim() || 'Cotización';
        if (quoteStatus.toLowerCase() === 'borrador') return null;
        const date = toTrackingDateYmd(
          quotation.quotation_date || quotation.closing_date || quotation.created_at || ''
        );
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
        const orgLabel = quotation.organization_name || 'Organización';
        const organization = (organizationsData || []).find((row) => Number(row.id) === organizationId);
        const assignedId = organization?.assigned_user_id ? Number(organization.assigned_user_id) : null;
        const assignedUser = assignedId
          ? (usersData || []).find((userRow) => Number(userRow.id) === assignedId)
          : null;
        const ownerName =
          String(organization?.assignee_name || '').trim() ||
          assignedUser?.full_name ||
          assignedUser?.user_code ||
          '';
        const code = quotation.quotation_code || `COT-${quotation.id || idx}`;
        return {
          id: `quot-${quotation.id || idx}`,
          kind: 'quotation',
          date,
          title: orgLabel,
          subtitle: [code, 'Cotización', ownerName].filter(Boolean).join(' · '),
          status: quoteStatus,
          color: '#7c3aed',
          organizationId,
          organizationName: orgLabel,
          contactId: quotation.contact_id,
          owner: ownerName,
          activityStatus: 'Cotizacion',
        };
      })
      .filter(Boolean);

    let events = activityEvents;
    if (calendarStatusFilter !== 'all') {
      events = activityEvents.filter(
        (evt) => String(evt.activityStatus || '').trim() === calendarStatusFilter
      );
    }
    if (calendarShowQuotations) {
      events = [...events, ...quotationEvents];
    }
    return events;
  }, [
    trackingRows,
    quotationsData,
    organizationsData,
    usersData,
    calendarStatusFilter,
    calendarShowQuotations,
  ]);

  const prospectSummaryRows = useMemo(() => {
    const periods = selectedPeriod.columns || [];
    const periodStart = periods[0]?.start || selectedPeriod.range?.start || null;

    const inBucket = (dateValue, bucket) => {
      const date = parseDateSafe(dateValue);
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const upToBucket = (dateValue, bucket) => {
      const date = parseDateSafe(dateValue);
      if (!date || date > bucket.end) return false;
      if (periodStart && date < periodStart) return false;
      return true;
    };
    const stageOf = (organization) => resolveOrganizationPipelineStage(organization);

    if (summaryMetric === 'amount') {
      const sumBy = (predicate) =>
        periods.map((bucket) =>
          prospectUniverse
            .filter(
              (organization) =>
                inBucket(getProspectAnchorDate(organization), bucket) && predicate(organization)
            )
            .reduce((acc, organization) => acc + Number(organization.sales_amount || 0), 0)
        );
      const cumSumBy = (predicate) =>
        periods.map((bucket) =>
          prospectUniverse
            .filter(
              (organization) =>
                upToBucket(getProspectAnchorDate(organization), bucket) && predicate(organization)
            )
            .reduce((acc, organization) => acc + Number(organization.sales_amount || 0), 0)
        );
      return [
        {
          label: 'Monto potencial nuevos (CLP)',
          tone: 'neutral',
          values: sumBy(() => true).map(formatSummaryMoney),
        },
        ...pipelineStages.map((stage) => ({
          label: `Monto ${stage}`,
          tone: stage === 'Cierre' ? 'client' : stage === 'Perdido' ? 'prospect' : 'neutral',
          values: sumBy((organization) => stageOf(organization) === stage).map(formatSummaryMoney),
        })),
        {
          label: 'Monto potencial acum. período (CLP)',
          tone: 'total',
          values: cumSumBy(() => true).map(formatSummaryMoney),
        },
      ];
    }

    const countBy = (predicate) =>
      periods.map(
        (bucket) =>
          prospectUniverse.filter(
            (organization) =>
              inBucket(getProspectAnchorDate(organization), bucket) && predicate(organization)
          ).length
      );
    const cumCountBy = (predicate) =>
      periods.map(
        (bucket) =>
          prospectUniverse.filter(
            (organization) =>
              upToBucket(getProspectAnchorDate(organization), bucket) && predicate(organization)
          ).length
      );
    const trackingCountBy = (predicate) =>
      periods.map(
        (bucket) =>
          (trackingRows || []).filter((row) => {
            const date = getTrackingAnchorDate(row);
            return inBucket(date, bucket) && predicate(row);
          }).length
      );

    const closed = countBy((organization) => stageOf(organization) === 'Cierre');
    const totalNew = countBy(() => true);
    const closeRate = totalNew.map((total, index) => {
      if (!total) return '—';
      return `${((closed[index] / total) * 100).toFixed(1)}%`;
    });

    return [
      {
        label: 'Prospectos nuevos',
        tone: 'neutral',
        values: totalNew.map(formatSummaryCount),
      },
      ...pipelineStages.map((stage) => ({
        label: `Etapa ${stage}`,
        tone: stage === 'Cierre' ? 'client' : stage === 'Perdido' ? 'prospect' : 'neutral',
        values: countBy((organization) => stageOf(organization) === stage).map(formatSummaryCount),
      })),
      {
        label: 'Tasa de cierre (nuevos)',
        tone: 'total',
        values: closeRate,
      },
      {
        label: 'Seguimientos vencidos',
        tone: 'prospect',
        values: trackingCountBy(
          (row) => String(row.due_state || '').trim().toLowerCase() === 'vencida'
        ).map(formatSummaryCount),
      },
      {
        label: 'Seguimientos pendientes',
        tone: 'neutral',
        values: trackingCountBy((row) => {
          const due = String(row.due_state || '').trim().toLowerCase();
          return due && due !== 'vencida';
        }).map(formatSummaryCount),
      },
      {
        label: 'Prospectos acum. período',
        tone: 'total',
        values: cumCountBy(() => true).map(formatSummaryCount),
      },
      {
        label: 'Cerrados acum. período',
        tone: 'total',
        values: cumCountBy((organization) => stageOf(organization) === 'Cierre').map(
          formatSummaryCount
        ),
      },
    ];
  }, [prospectUniverse, selectedPeriod, summaryMetric, trackingRows, pipelineStages]);

  const handleAddIdentification = () => {
    setEditingOrgId(null);
    setOrgFormError('');
    setOrgOriginalContactIds([]);
    setOrgFormData({
      ...EMPTY_ORG_FORM,
      country: unit === 'aditivos' ? '' : 'Chile',
      created_by: sessionUserName || '',
      assigned_user_id: user?.id ? String(user.id) : '',
    });
    setShowOrgFormModal(true);
  };

  const handleEditIdentification = (rowToEdit) => {
    if (!rowToEdit) {
      window.alert('Selecciona una fila de identificación para editar.');
      return;
    }
    const orgId = Number(rowToEdit.organization_id);
    const selectedOrg = organizationsData.find((organization) => Number(organization.id) === orgId);
    if (!selectedOrg) {
      window.alert('No se encontró la organización seleccionada.');
      return;
    }
    setEditingOrgId(selectedOrg.id);
    setOrgFormError('');
    setOrgFormData({
      org_code: selectedOrg.org_code || '',
      org_name: selectedOrg.org_name || '',
      rut: formatRut(selectedOrg.rut || ''),
      segment: selectedOrg.segment || 'B2B',
      channel: selectedOrg.channel || '',
      created_by: selectedOrg.created_by || '',
      assigned_user_id: selectedOrg.assigned_user_id ? String(selectedOrg.assigned_user_id) : '',
      website: selectedOrg.website || '',
      linkedin: selectedOrg.linkedin || '',
      industry: selectedOrg.industry || '',
      subindustry: selectedOrg.subindustry || '',
      email: selectedOrg.email || '',
      phone: selectedOrg.phone || '',
      address: selectedOrg.address || '',
      commune: selectedOrg.commune || '',
      region: selectedOrg.region || '',
      country: selectedOrg.country || (unit === 'aditivos' ? '' : 'Chile'),
      contacts: contactsData
        .filter((contact) => Number(contact.organization_id) === Number(selectedOrg.id))
        .map((contact) => ({
          id: contact.id,
          full_name: contact.full_name || '',
          role: contact.role || '',
          email: contact.email || '',
          phone: contact.phone || '',
          linkedin: contact.linkedin || '',
          status: contact.status || 'Activo',
        })),
    });
    setOrgOriginalContactIds(
      contactsData
        .filter((contact) => Number(contact.organization_id) === Number(selectedOrg.id))
        .map((contact) => Number(contact.id))
        .filter((id) => Number.isFinite(id))
    );
    setShowOrgFormModal(true);
  };

  const handleDeleteIdentification = async (payload) => {
    const rowsToDelete = Array.isArray(payload) ? payload : [payload];
    const idsToDelete = Array.from(
      new Set(rowsToDelete.map((row) => Number(row?.organization_id)).filter((id) => Number.isFinite(id) && id > 0))
    );
    if (!idsToDelete.length) {
      window.alert('Selecciona al menos una fila para eliminar.');
      return;
    }
    try {
      await Promise.all(
        idsToDelete.map((orgId) =>
          apiClient(`${API_BASE_URL}/api/organizations/${orgId}`, { method: 'DELETE' })
        )
      );
      await loadBaseData();
    } catch (error) {
      window.alert(error.message || 'Error eliminando organizaciones.');
    }
  };

  const openCreateModalContactForm = () => {
    if (!selectedOrganization) {
      window.alert('Primero selecciona una organización.');
      return;
    }
    setEditingContactId(null);
    setContactFormError('');
    setContactFormData({
      ...EMPTY_CONTACT_FORM,
      organization_id: String(selectedOrganization.id),
    });
    setShowContactFormModal(true);
  };

  const openEditModalContactForm = (rowToEdit) => {
    if (!rowToEdit) {
      window.alert('Selecciona un contacto para editar.');
      return;
    }
    setEditingContactId(rowToEdit.id);
    setContactFormError('');
    setContactFormData({
      contact_code: rowToEdit.contact_code || '',
      full_name: rowToEdit.full_name || '',
      role: rowToEdit.role || '',
      email: rowToEdit.email || '',
      linkedin: rowToEdit.linkedin || '',
      phone: rowToEdit.phone || '',
      organization_id: String(rowToEdit.organization_id || selectedOrganization?.id || ''),
      status: rowToEdit.status || 'Activo',
      source: rowToEdit.source || '',
      source_details: rowToEdit.source_details || '',
    });
    setShowContactFormModal(true);
  };

  const handleModalContactFormChange = (field, value) => {
    setContactFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveModalContact = async () => {
    if (!contactFormData.full_name.trim()) {
      setContactFormError('El nombre del contacto es obligatorio.');
      return;
    }
    if (!selectedOrganization?.id) {
      setContactFormError('No hay una organización seleccionada.');
      return;
    }

    setIsSavingContact(true);
    setContactFormError('');

    const payload = {
      full_name: contactFormData.full_name.trim(),
      role: contactFormData.role.trim(),
      email: contactFormData.email.trim(),
      linkedin: contactFormData.linkedin.trim(),
      phone: contactFormData.phone.trim(),
      organization_id: Number(selectedOrganization.id),
      status: contactFormData.status || 'Activo',
      source: String(contactFormData.source || '').trim(),
      source_details: String(contactFormData.source_details || '').trim(),
    };

    const endpoint = editingContactId
      ? `${API_BASE_URL}/api/contacts/${editingContactId}`
      : `${API_BASE_URL}/api/contacts/`;
    const method = editingContactId ? 'PUT' : 'POST';

    try {
      const savedContact = await apiClient(endpoint, {
        method,
        body: JSON.stringify(payload),
      });
      setShowContactFormModal(false);
      setEditingContactId(null);
      setContactFormData({ ...EMPTY_CONTACT_FORM });
      await loadBaseData();
      if (savedContact?.id) {
        setSelectedContactId(savedContact.id);
      }
    } catch (error) {
      setContactFormError(error.message || 'No se pudo guardar el contacto.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleDeleteModalContacts = async (payload) => {
    const rowsToDelete = Array.isArray(payload) ? payload : [payload];
    const ids = rowsToDelete.map((row) => Number(row?.id)).filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      window.alert('Selecciona al menos un contacto para eliminar.');
      return;
    }
    try {
      for (const id of ids) {
        await apiClient(`${API_BASE_URL}/api/contacts/${id}`, { method: 'DELETE' });
      }
      if (ids.includes(Number(selectedContactId))) {
        setSelectedContactId(null);
      }
      await loadBaseData();
    } catch (error) {
      window.alert(error.message || 'Error eliminando contactos.');
    }
  };

  const openCreateActivityForm = () => {
    const presetIds = selectedContactId ? [String(selectedContactId)] : [];
    const sessionOwner =
      activityExecutiveOptions.find((opt) => String(opt.value) === sessionUserName)?.value ||
      sessionUserName ||
      activityExecutiveOptions.find((opt) => String(opt.value || '').trim())?.value ||
      '';
    setActivityFormError('');
    setEditingActivityId(null);
    setActivityFormMode('create');
    setActivityFormData({
      ...EMPTY_ACTIVITY_FORM,
      contact_id: presetIds[0] || '',
      contact_ids: presetIds,
      activity_date: new Date().toISOString().slice(0, 10),
      status: 'Pendiente',
      owner_name: sessionOwner,
    });
    setShowActivityFormModal(true);
  };

  const handleActivityFormChange = (field, value) => {
    setActivityFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveActivity = async () => {
    const selectedIds = (
      Array.isArray(activityFormData.contact_ids) && activityFormData.contact_ids.length
        ? activityFormData.contact_ids
        : activityFormData.contact_id
          ? [activityFormData.contact_id]
          : []
    )
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);

    const organizationId = Number(selectedOrganization?.id || 0);
    if (!selectedIds.length && organizationId <= 0) {
      setActivityFormError('Abre un prospecto para registrar la actividad.');
      return;
    }
    if (!String(activityFormData.action || '').trim()) {
      setActivityFormError('Debes seleccionar la acción realizada.');
      return;
    }
    if (!activityFormData.no_response && !String(activityFormData.response || '').trim()) {
      setActivityFormError('Ingresa la respuesta o marca "Sin Respuesta".');
      return;
    }
    if (activityFormData.has_next_steps && !String(activityFormData.next_step || '').trim()) {
      setActivityFormError('Indica el próximo paso o desactiva "Siguientes Pasos".');
      return;
    }
    if (activityFormData.has_next_steps && !String(activityFormData.next_step_date || '').trim()) {
      setActivityFormError('Indica la fecha programada del siguiente paso.');
      return;
    }
    if (
      activityFormData.has_follow_up_reminder &&
      !String(activityFormData.follow_up_reminder_date || '').trim()
    ) {
      setActivityFormError('Indica la fecha de recordatorio o desactiva "Recordatorio Seguimiento".');
      return;
    }

    const primaryContactId = selectedIds[0] || null;
    const scheduleNextStep = Boolean(activityFormData.has_next_steps);
    const nextStepAction = String(activityFormData.next_step || '').trim();
    const nextStepDetail = String(activityFormData.next_step_detail || '').trim();
    const nextStepDate = String(activityFormData.next_step_date || '').trim();

    setIsSavingActivity(true);
    setActivityFormError('');
    try {
      const endpoint = editingActivityId
        ? `${API_BASE_URL}/api/contacts/activities/${editingActivityId}`
        : primaryContactId
          ? `${API_BASE_URL}/api/contacts/${primaryContactId}/activities`
          : `${API_BASE_URL}/api/contacts/organizations/${organizationId}/activities`;
      const method = editingActivityId ? 'PUT' : 'POST';
      const responseText = activityFormData.no_response
        ? ''
        : String(activityFormData.response || '').trim();
      const sharedActivityFields = {
        contact_ids: selectedIds,
        organization_id: organizationId || undefined,
        owner_name: String(activityFormData.owner_name || '').trim(),
        funnel_stage: String(activityFormData.funnel_stage || '').trim(),
      };
      await apiClient(endpoint, {
        method,
        body: JSON.stringify({
          ...sharedActivityFields,
          activity_date: String(activityFormData.activity_date || '').trim(),
          action: String(activityFormData.action || '').trim(),
          notes: responseText,
          response: responseText,
          no_response: Boolean(activityFormData.no_response),
          // Los siguientes pasos se materializan como actividad nueva (abajo).
          next_step: '',
          next_step_detail: '',
          next_step_date: '',
          has_next_steps: false,
          has_follow_up_reminder: Boolean(activityFormData.has_follow_up_reminder),
          follow_up_reminder_date: activityFormData.has_follow_up_reminder
            ? String(activityFormData.follow_up_reminder_date || '').trim()
            : '',
          status: String(activityFormData.status || 'Pendiente').trim(),
        }),
      });

      if (scheduleNextStep) {
        const scheduledEndpoint = primaryContactId
          ? `${API_BASE_URL}/api/contacts/${primaryContactId}/activities`
          : `${API_BASE_URL}/api/contacts/organizations/${organizationId}/activities`;
        await apiClient(scheduledEndpoint, {
          method: 'POST',
          body: JSON.stringify({
            ...sharedActivityFields,
            activity_date: nextStepDate,
            action: nextStepAction,
            notes: nextStepDetail,
            response: nextStepDetail,
            no_response: !nextStepDetail,
            next_step: '',
            next_step_detail: '',
            next_step_date: '',
            has_next_steps: false,
            has_follow_up_reminder: false,
            follow_up_reminder_date: '',
            status: 'Programada',
          }),
        });
      }

      setShowActivityFormModal(false);
      setEditingActivityId(null);
      setActivityFormMode('create');
      setActivityFormData({ ...EMPTY_ACTIVITY_FORM });
      setSelectedContactId(primaryContactId || selectedContactId);
      // Mantener vista de toda la organización para no ocultar actividades previas
      // al filtrar solo por el contacto recién usado.
      setActivityContactFilterId('');
      await loadActivitiesForOrganization(selectedOrganization?.id);
      const refreshed = await loadBaseData();
      const updatedOrganization = refreshed.organizations.find(
        (organization) => Number(organization.id) === Number(selectedOrganization?.id)
      );
      if (updatedOrganization) {
        setSelectedOrganization(updatedOrganization);
      }
    } catch (error) {
      setActivityFormError(error.message || 'No se pudo guardar la actividad.');
    } finally {
      setIsSavingActivity(false);
    }
  };

  const handleViewActivity = (rowToView) => {
    if (!rowToView) {
      window.alert('Selecciona una actividad para ver el detalle.');
      return;
    }
    const linkedIds = Array.isArray(rowToView.contact_ids) && rowToView.contact_ids.length
      ? rowToView.contact_ids.map((id) => String(id))
      : rowToView.contact_id
        ? [String(rowToView.contact_id)]
        : [];
    const hasNext =
      Boolean(rowToView.has_next_steps) ||
      Boolean(String(rowToView.next_step || '').trim()) ||
      Boolean(String(rowToView.next_step_detail || '').trim()) ||
      Boolean(rowToView.next_step_date);
    const hasReminder =
      Boolean(rowToView.has_follow_up_reminder) || Boolean(rowToView.follow_up_reminder_date);
    setActivityFormError('');
    setEditingActivityId(rowToView.id || null);
    setActivityFormMode('view');
    setActivityFormData({
      contact_id: linkedIds[0] || '',
      contact_ids: linkedIds,
      activity_date: rowToView.activity_date || '',
      action: rowToView.action || '',
      status: rowToView.status || 'Pendiente',
      notes: rowToView.notes || '',
      response: rowToView.response || rowToView.notes || '',
      no_response: Boolean(rowToView.no_response),
      next_step: rowToView.next_step || '',
      next_step_detail: rowToView.next_step_detail || '',
      next_step_date: rowToView.next_step_date || '',
      has_next_steps: hasNext,
      has_follow_up_reminder: hasReminder,
      follow_up_reminder_date: rowToView.follow_up_reminder_date || '',
      owner_name: rowToView.owner_name || '',
      funnel_stage: rowToView.funnel_stage || '',
    });
    setShowActivityFormModal(true);
  };

  const handleDeleteActivity = () => {
    window.alert('Botón Eliminar de Actividades habilitado. Definiremos su lógica en el siguiente paso.');
  };

  const handleAssignEventOwner = async (event, ownerName) => {
    const row = event?.sourceRow;
    const activityId = Number(event?.activityId || row?.id || 0);
    if (!activityId) return;
    const linkedIds =
      Array.isArray(row?.contact_ids) && row.contact_ids.length
        ? row.contact_ids.map((id) => Number(id)).filter(Boolean)
        : row?.contact_id
          ? [Number(row.contact_id)]
          : [];
    setAssigningEventId(activityId);
    try {
      await apiClient(`${API_BASE_URL}/api/contacts/activities/${activityId}`, {
        method: 'PUT',
        body: JSON.stringify({
          activity_date: row?.activity_date || '',
          action: row?.action || '',
          notes: row?.notes || row?.response || '',
          response: row?.response || row?.notes || '',
          no_response: Boolean(row?.no_response),
          next_step: row?.next_step || '',
          next_step_detail: row?.next_step_detail || '',
          next_step_date: row?.next_step_date || '',
          has_next_steps: Boolean(row?.has_next_steps),
          has_follow_up_reminder: Boolean(row?.has_follow_up_reminder),
          follow_up_reminder_date: row?.follow_up_reminder_date || '',
          status: row?.status || 'Pendiente',
          owner_name: String(ownerName || '').trim(),
          funnel_stage: row?.funnel_stage || '',
          organization_id: Number(row?.organization_id || event?.organizationId || 0),
          contact_ids: linkedIds,
        }),
      });
      await loadTrackingRows();
    } catch (error) {
      window.alert(error?.message || 'No se pudo asignar el responsable.');
    } finally {
      setAssigningEventId(null);
    }
  };

  const closeActivityForm = () => {
    setShowActivityFormModal(false);
    setEditingActivityId(null);
    setActivityFormMode('create');
    setActivityFormError('');
    setActivityFormData({ ...EMPTY_ACTIVITY_FORM });
  };

  const handleOrgFormChange = (field, value) => {
    if (field === 'rut') {
      setOrgFormData((prev) => ({ ...prev, rut: formatRut(value) }));
      return;
    }
    setOrgFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveOrganization = async () => {
    if (!orgFormData.org_name.trim()) {
      setOrgFormError('El nombre de la organización es obligatorio.');
      return;
    }
    const newNameNormalized = normalizeOrgName(orgFormData.org_name);
    const duplicatedExact = organizationsData.find(
      (organization) =>
        Number(organization.id) !== Number(editingOrgId || 0) &&
        normalizeOrgName(organization.org_name) === newNameNormalized
    );
    if (duplicatedExact) {
      setOrgFormError(`Ya existe una organización con ese nombre: ${duplicatedExact.org_name}`);
      return;
    }

    setIsSavingOrg(true);
    setOrgFormError('');

    const payload = {
      org_name: orgFormData.org_name.trim(),
      rut: formatRut(orgFormData.rut).trim(),
      segment: (orgFormData.segment || 'B2B').trim(),
      channel: serializeOrganizationChannels(orgFormData.channel),
      created_by: orgFormData.created_by.trim() || sessionUserName || 'system',
      assigned_user_id: orgFormData.assigned_user_id
        ? Number(orgFormData.assigned_user_id)
        : null,
      website: orgFormData.website.trim(),
      linkedin: orgFormData.linkedin.trim(),
      industry: orgFormData.industry.trim(),
      subindustry: orgFormData.subindustry.trim(),
      email: orgFormData.email.trim(),
      phone: orgFormData.phone.trim(),
      address: orgFormData.address.trim(),
      commune: orgFormData.commune.trim(),
      region: orgFormData.region.trim(),
      country: orgFormData.country.trim(),
    };
    const contactsPayload = (orgFormData.contacts || [])
      .map((contact) => ({
        id: contact.id ? Number(contact.id) : null,
        full_name: String(contact.full_name || '').trim(),
        role: String(contact.role || '').trim(),
        email: String(contact.email || '').trim(),
        phone: String(contact.phone || '').trim(),
        linkedin: String(contact.linkedin || '').trim(),
        status: String(contact.status || 'Activo').trim() || 'Activo',
      }))
      .filter((contact) => contact.full_name);

    const endpoint = editingOrgId
      ? `${API_BASE_URL}/api/organizations/${editingOrgId}`
      : `${API_BASE_URL}/api/organizations/`;
    const method = editingOrgId ? 'PUT' : 'POST';

    try {
      const savedOrganization = await apiClient(endpoint, {
        method,
        body: JSON.stringify(payload),
      });
      const organizationId = Number(editingOrgId || savedOrganization?.id);
      if (!organizationId) {
        throw new Error('No se pudo determinar el ID de la organización para guardar contactos.');
      }

      const currentContactIds = new Set();
      const contactOperations = [];
      contactsPayload.forEach((contact) => {
        const contactPayload = {
          full_name: contact.full_name,
          role: contact.role,
          email: contact.email,
          phone: contact.phone,
          linkedin: contact.linkedin,
          organization_id: organizationId,
          status: contact.status,
        };
        if (contact.id) {
          currentContactIds.add(Number(contact.id));
          contactOperations.push(() =>
            apiClient(`${API_BASE_URL}/api/contacts/${contact.id}`, {
              method: 'PUT',
              body: JSON.stringify(contactPayload),
            })
          );
        } else {
          contactOperations.push(() =>
            apiClient(`${API_BASE_URL}/api/contacts/`, {
              method: 'POST',
              body: JSON.stringify(contactPayload),
            })
          );
        }
      });

      const deletedContactIds = orgOriginalContactIds.filter((id) => !currentContactIds.has(Number(id)));
      deletedContactIds.forEach((contactId) => {
        contactOperations.push(() =>
          apiClient(`${API_BASE_URL}/api/contacts/${contactId}`, { method: 'DELETE' })
        );
      });

      for (const operation of contactOperations) {
        await operation();
      }

      setShowOrgFormModal(false);
      setEditingOrgId(null);
      setOrgOriginalContactIds([]);
      setOrgFormData({ ...EMPTY_ORG_FORM });
      await loadBaseData();
    } catch (error) {
      setOrgFormError(error.message || 'No se pudo guardar la organización.');
    } finally {
      setIsSavingOrg(false);
    }
  };

  const handleViewIdentification = (rowToView) => {
    if (!rowToView) {
      window.alert('Selecciona una fila para ver el detalle.');
      return;
    }
    let matchedOrganization = null;
    const directId = Number(rowToView.organization_id || rowToView.id || 0);
    if (directId > 0) {
      matchedOrganization =
        organizationsData.find((organization) => Number(organization.id) === directId) || null;
    } else {
      const orgByExact = organizationsData.find(
        (organization) =>
          String(organization.org_name || '').trim().toLowerCase() ===
          String(rowToView.company || '').trim().toLowerCase()
      );
      const orgByContains = organizationsData.find((organization) =>
        String(organization.org_name || '')
          .toLowerCase()
          .includes(String(rowToView.company || '').toLowerCase())
      );
      matchedOrganization = orgByExact || orgByContains || null;
    }

    if (!matchedOrganization) {
      window.alert('No se encontró la empresa en la base de datos para desplegar el formulario.');
      return;
    }

    const contactsForOrg = contactsData.filter(
      (contact) => Number(contact.organization_id) === Number(matchedOrganization.id)
    );
    const preferredContactId = Number(rowToView.contact_id || rowToView.contactId || 0);
    const preferredContact = preferredContactId
      ? contactsForOrg.find((contact) => Number(contact.id) === preferredContactId)
      : null;
    setSelectedOrganization(matchedOrganization);
    setSelectedContactId(preferredContact?.id || contactsForOrg[0]?.id || null);
    setActivityContactFilterId('');
    setShowCompanyModal(true);
    // Siempre recargar: si se reabre la misma org, selectedOrganization no cambia de
    // referencia y el useEffect no dispara; sin esto la lista queda vacía hasta F5.
    void loadActivitiesForOrganization(matchedOrganization.id);
  };

  const identificationRowsForTable3 = useMemo(
    () =>
      organizationsData
        .filter((organization) =>
          IDENTIFICATION_ALLOWED_SEGMENTS.includes(String(organization.segment || '').trim())
        )
        .filter((organization) => {
          const industryMatch =
            !selectedIndustry || String(organization.industry || '-') === selectedIndustry;
          if (!industryMatch) return false;
          if (!selectedExecutive) return true;
          return String(organization.assigned_user_id || '') === selectedExecutive;
        })
        .map((organization) => {
          const orgContacts = contactsData
            .filter((contact) => Number(contact.organization_id) === Number(organization.id))
            .sort((a, b) => Number(a.id) - Number(b.id));
          const contactNames = orgContacts
            .slice(0, 2)
            .map((contact) => String(contact.full_name || '').trim())
            .filter(Boolean);
          const assignedId = organization.assigned_user_id
            ? Number(organization.assigned_user_id)
            : null;
          const assignedUser = assignedId
            ? usersData.find((userRow) => Number(userRow.id) === assignedId)
            : null;
          const assigneeName =
            String(organization.assignee_name || '').trim() ||
            assignedUser?.full_name ||
            assignedUser?.user_code ||
            assignedUser?.username ||
            '';
          return {
            ...organization,
            created_by: String(organization.created_by || '').trim() || 'Sin registro',
            assignee_name: assigneeName || 'Sin asignar',
            assigned_user_id: assignedId,
            contactNames,
            contacts_count: orgContacts.length || Number(organization.contacts_count || 0),
            quoted_total: quotedTotalsByOrg[Number(organization.id)] || 0,
            funnel_stage: resolveOrganizationPipelineStage(organization),
          };
        }),
    [
      organizationsData,
      contactsData,
      selectedIndustry,
      selectedExecutive,
      usersData,
      quotedTotalsByOrg,
      quotationsData,
      pipelineStages,
    ]
  );

  const listTabs = useMemo(
    () => [
      { key: 'prospects', label: 'Prospectos', count: identificationRowsForTable3.length },
      { key: 'tracking', label: 'Seguimiento', count: trackingRows.length },
      {
        key: 'pipeline',
        label: 'Pipeline',
        count: pipelineStages.reduce(
          (acc, stage) => acc + (pipelineCardsByStage[stage]?.length || 0),
          0
        ),
      },
    ],
    [identificationRowsForTable3.length, trackingRows.length, pipelineCardsByStage, pipelineStages]
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '4px',
        }}
      >
        <Heading level={2} isDarkMode={isDarkMode}>
          Prospección Comercial
        </Heading>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '160px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' ||
          periodFilter === 'annual_ytd' ||
          periodFilter === 'quarterly' ||
          periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
              >
                <option value="all">Todos</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      <PillTabBar
        ariaLabel="Secciones de prospección"
        items={SECTION_ITEMS}
        activeKey={sectionTab}
        onChange={setSectionTab}
        style={{ margin: '12px 0 16px' }}
      />

      {sectionTab === 'events' ? (
        <ProspectEventsPanel
          events={trackingCalendarEvents}
          executiveOptions={activityExecutiveOptions}
          assigningId={assigningEventId}
          onAssignOwner={handleAssignEventOwner}
          onEventClick={(evt) => {
            if (evt?.kind === 'activity' && evt.sourceRow) {
              handleViewActivity(evt.sourceRow);
              return;
            }
            handleViewIdentification({
              organization_id: evt.organizationId,
              id: evt.organizationId,
              company: evt.organizationName,
              contact_id: evt.contactId,
            });
          }}
          isDarkMode={isDarkMode}
          calendar={
            <ScheduleCalendar
              events={trackingCalendarEvents}
              isDarkMode={isDarkMode}
              title="Calendario de eventos"
              allowedViews={['week', 'biweek', 'month']}
              defaultView="month"
              collapsed={calendarCollapsed}
              onToggleCollapsed={() => setCalendarCollapsed((prev) => !prev)}
              emptyLabel="Sin eventos"
              toolbarExtra={
                <>
                  <select
                    value={calendarStatusFilter}
                    onChange={(event) => setCalendarStatusFilter(event.target.value)}
                    aria-label="Filtrar actividades por estado"
                    style={{
                      height: 32,
                      borderRadius: 8,
                      border: `1px solid ${ui.border}`,
                      background: ui.inputBg,
                      color: ui.text,
                      padding: '0 10px',
                      fontSize: 12,
                    }}
                  >
                    <option value="all">Todas las actividades</option>
                    <option value="Programada">Programada</option>
                    <option value="Pendiente">Pendiente</option>
                    <option value="Completa">Completa</option>
                  </select>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      height: 32,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: `1px solid ${ui.border}`,
                      background: ui.inputBg,
                      color: ui.text,
                      fontSize: 12,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={calendarShowQuotations}
                      onChange={(event) => setCalendarShowQuotations(event.target.checked)}
                    />
                    Cotizaciones
                  </label>
                </>
              }
              onEventClick={(evt) => {
                if (evt?.kind === 'activity' && evt.sourceRow) {
                  handleViewActivity(evt.sourceRow);
                  return;
                }
                handleViewIdentification({
                  organization_id: evt.organizationId,
                  id: evt.organizationId,
                  company: evt.organizationName,
                  contact_id: evt.contactId,
                });
              }}
            />
          }
        />
      ) : (
        <>
      <ScheduleCalendar
        events={trackingCalendarEvents}
        isDarkMode={isDarkMode}
        title="Calendario de seguimiento"
        allowedViews={['week', 'biweek']}
        defaultView="week"
        collapsed={calendarCollapsed}
        onToggleCollapsed={() => setCalendarCollapsed((prev) => !prev)}
        emptyLabel="Sin actividades"
        toolbarExtra={
          <>
            <select
              value={calendarStatusFilter}
              onChange={(event) => setCalendarStatusFilter(event.target.value)}
              aria-label="Filtrar actividades por estado"
              style={{
                height: 32,
                borderRadius: 8,
                border: `1px solid ${ui.border}`,
                background: ui.inputBg,
                color: ui.text,
                padding: '0 10px',
                fontSize: 12,
              }}
            >
              <option value="all">Todas las actividades</option>
              <option value="Programada">Programada</option>
              <option value="Pendiente">Pendiente</option>
              <option value="Completa">Completa</option>
            </select>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 32,
                padding: '0 10px',
                borderRadius: 8,
                border: `1px solid ${ui.border}`,
                background: ui.inputBg,
                color: ui.text,
                fontSize: 12,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={calendarShowQuotations}
                onChange={(event) => setCalendarShowQuotations(event.target.checked)}
              />
              Cotizaciones
            </label>
          </>
        }
        onEventClick={(evt) =>
          handleViewIdentification({
            organization_id: evt.organizationId,
            id: evt.organizationId,
            company: evt.organizationName,
            contact_id: evt.contactId,
          })
        }
      />

      <PeriodSummaryTable
        periods={selectedPeriod.columns}
        rows={prospectSummaryRows}
        title={selectedPeriod.title || 'Resumen de prospección'}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((prev) => !prev)}
        metricOptions={PROSPECT_SUMMARY_METRIC_OPTIONS}
        metric={summaryMetric}
        onMetricChange={setSummaryMetric}
      />

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Prospección</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
              {listTab === 'tracking'
                ? `${trackingRows.length} seguimientos`
                : listTab === 'pipeline'
                  ? `${pipelineStages.reduce(
                      (acc, stage) => acc + (pipelineCardsByStage[stage]?.length || 0),
                      0
                    )} oportunidades`
                  : `${identificationRowsForTable3.length} prospectos`}
            </p>
          </div>
          {listTab === 'prospects' ? (
            <button
              type="button"
              onClick={handleAddIdentification}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Plus size={16} /> Nueva organización
            </button>
          ) : null}
        </div>

        <div
          role="tablist"
          aria-label="Vistas de prospección"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {listTabs.map((item) => {
            const active = listTab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setListTab(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        {listTab === 'prospects' ? (
          <Table3
            rows={identificationRowsForTable3}
            loading={false}
            error=""
            isDarkMode={isDarkMode}
            hideChrome
            statusField="funnel_stage"
            onRowNameClick={handleViewIdentification}
            toolbarExtra={
              <>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    fontSize: '11px',
                    color: ui.muted,
                    fontWeight: 600,
                  }}
                >
                  Industria
                  <select
                    value={selectedIndustry}
                    onChange={(event) => setSelectedIndustry(event.target.value)}
                    style={{
                      height: '36px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: `1px solid ${ui.border}`,
                      background: ui.inputBg || ui.panel,
                      color: ui.text,
                      fontSize: '13px',
                      minWidth: '140px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">Todas</option>
                    {industryOptions.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </label>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    fontSize: '11px',
                    color: ui.muted,
                    fontWeight: 600,
                  }}
                >
                  Responsable
                  <select
                    value={selectedExecutive}
                    onChange={(event) => setSelectedExecutive(event.target.value)}
                    style={{
                      height: '36px',
                      padding: '0 10px',
                      borderRadius: '8px',
                      border: `1px solid ${ui.border}`,
                      background: ui.inputBg || ui.panel,
                      color: ui.text,
                      fontSize: '13px',
                      minWidth: '140px',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">Todos</option>
                    {executiveOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            }
          />
        ) : null}
        {listTab === 'tracking' ? (
          <ProspectTrackingTable3
            rows={trackingRows}
            loading={trackingLoading}
            error={trackingError}
            isDarkMode={isDarkMode}
            hideChrome
            onRowClick={handleViewActivity}
          />
        ) : null}
        {listTab === 'pipeline' ? (
          <div style={{ padding: '12px' }}>
            <PipelineBoard
              cardsByStage={pipelineCardsByStage}
              stages={pipelineStages}
              stageStyles={pipelineStageStyles}
              isDarkMode={isDarkMode}
              onOpenCard={(card) =>
                handleViewIdentification({
                  organization_id: card.id,
                  id: card.id,
                  company: card.company,
                })
              }
            />
          </div>
        ) : null}
      </div>
        </>
      )}

      <CompanyInfoModal
        isOpen={showCompanyModal}
        onClose={() => {
          setShowCompanyModal(false);
          setSelectedOrganization(null);
          setSelectedContactId(null);
          setActivityContactFilterId('');
          setOrganizationActivities([]);
        }}
        isDarkMode={isDarkMode}
        organization={selectedOrganization}
        contacts={modalContacts}
        activities={organizationActivities}
        selectedContactId={selectedContactId}
        onSelectContact={(id) => {
          setSelectedContactId(id);
          setActivityContactFilterId(id ? String(id) : '');
        }}
        activitiesLoading={activitiesLoading}
        activityContactFilterId={activityContactFilterId}
        onChangeActivityContactFilter={(value) => {
          setActivityContactFilterId(value);
          setSelectedContactId(value ? Number(value) : null);
        }}
        onAddContact={openCreateModalContactForm}
        onEditContact={openEditModalContactForm}
        onDeleteContact={handleDeleteModalContacts}
        onAddActivity={openCreateActivityForm}
        onViewActivity={handleViewActivity}
        onDeleteActivity={handleDeleteActivity}
        onContactsChanged={loadBaseData}
      />
      <OrganizationsForm
        isOpen={showOrgFormModal}
        onClose={() => setShowOrgFormModal(false)}
        onSave={handleSaveOrganization}
        isSaving={isSavingOrg}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingOrgId)}
        formData={orgFormData}
        onChange={handleOrgFormChange}
        formError={orgFormError}
        executiveOptions={orgExecutiveOptions}
        existingOrganizations={organizationsData}
        currentOrganizationId={editingOrgId}
      />
      <ContactsForm
        isOpen={showContactFormModal}
        onClose={() => setShowContactFormModal(false)}
        onSave={handleSaveModalContact}
        isSaving={isSavingContact}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingContactId)}
        formData={contactFormData}
        onChange={handleModalContactFormChange}
        formError={contactFormError}
        organizationOptions={modalOrganizationOptions}
        zIndex={13000}
      />
      <ContactActivitiesForm
        isOpen={showActivityFormModal}
        onClose={closeActivityForm}
        onSave={handleSaveActivity}
        isSaving={isSavingActivity}
        isDarkMode={isDarkMode}
        isEditing={activityFormMode !== 'create'}
        formData={activityFormData}
        onChange={handleActivityFormChange}
        formError={activityFormError}
        zIndex={13000}
        executiveOptions={activityExecutiveOptions}
        contactOptions={activityContactOptions}
        funnelStageOptions={activityFunnelStageOptions}
        readOnly={false}
      />
    </div>
  );
};

export default PaintProspectingWorkspace;
